import React, { useState } from 'react';
import './Antrag.css';
import FileUpload from "./FileUpload";
import OtherDocumentForm from './OtherDocumentForm';
import { IoChevronUp, IoChevronDown } from "react-icons/io5";
import { IoIosAdd, IoIosRemove } from "react-icons/io";
import { DOCUMENT_TYPES } from '../../constants/docTypes';

interface DocumentFormProps {
    formData: Array<{ name: string; description: string; guid: string }>;
    familyStatus: string | null;
    hasThesis: boolean;
    hasWorkExperience: boolean | null;
    hasExam: boolean | null;
    bachDegree: string | null;
    handleDelete: (fileName: any, name: string) => void;
    getDocumentsByDescription: (description: string) => Array<{ name: string; guid: string }>;
    otherDocumentData: Array<{ description: string; name: string; guid: string }>;
    onUploadSuccess?: (description: string, fileName: string, guid: string) => void;
    handleOtherDocumentChange: (index: number, e: React.ChangeEvent<HTMLInputElement>) => void;
    handleDeleteOther: (index: number) => void;
    handleOtherDocumentDelete: () => void;
    addOtherDocument: () => void;
    antragId: number;
    isViewOnly: boolean;
    showValidation?: boolean;
    handleOtherDocumentUploadSuccess: (index: number, fileName: string, guid: string) => void;
}

const DocumentForm: React.FC<DocumentFormProps> = ({ formData, handleDelete, familyStatus, hasThesis, hasWorkExperience, hasExam, bachDegree, getDocumentsByDescription, otherDocumentData, handleOtherDocumentChange, handleOtherDocumentDelete, addOtherDocument, handleDeleteOther, antragId, isViewOnly, showValidation, onUploadSuccess, handleOtherDocumentUploadSuccess }) => {

    const [collapse, setCollapse] = useState({
        personalData: isViewOnly ? false : false,
        ausbildung: isViewOnly ? false : true,
        workExperience: isViewOnly ? false : true,
        other: isViewOnly ? false : true
    });

    const toggleCollapse = (section: keyof typeof collapse) => {
        setCollapse(prevState => ({
            ...prevState,
            [section]: !prevState[section]
        }));
    };

    const calculateMaxHeight = (numItems: number) => {
        const baseHeight = 200; // Base height
        const itemHeight = 200; // Height for each new item
        return baseHeight + numItems * itemHeight;
    };

    const languageLevel = bachDegree === 'Hebammen' ? 'C1' : 'B2';

    const sprachnachweisDescription = `Gemeinsamer Europäischer Referenzrahmen für Sprachen, von einer der folgenden Einrichtungen: <br />
       Österreichisches Sprachdiplom Deutsch - ÖSD, Goethe Institut e.V., Telc GmbH, Österreichischer Integrationsfonds - ÖIF <br />
       spätestens <b>vor</b> Auflagenerfüllung<br />
       <b>Wir möchten darauf hinweisen, dass für die Eintragung ins Berufsregister ein Sprachnachweis Deutsch (${languageLevel}) notwendig ist.</b>`;

    return (
        <div>
            <p className='doc-text'>
                Bitte laden Sie die Dokumente als PDF hoch. Je PDF bitte das <b>Originaldokument</b> und <b>Übersetzung</b> und <b>Beglaubigung</b> zusammenfügen.<br />
                <b>Für Übersetzungen gilt:</b> Diese müssen von einem gerichtlich beeideten Dolmetscher ins Deutsche oder Englische angefertigt werden.<br />
                <b>Für Beglaubigungen gilt:</b> Je nach Land gelten unterschiedliche Beglaubigungsvorschriften, alle Informationen zu Beglaubigungen finden Sie auf der Webseite des {" "}
                <a
                    href="https://www.bmb.gv.at/Themen/schule/schulrecht/anauschubi/bvs.html"
                    target="_blank"
                    rel="noopener noreferrer "
                    style={{ color: "blue", textDecoration: "underline", fontWeight: "600" }}
                >
                    Bundesministeriums.
                </a><br />
                Die mit <b>*</b> gekennzeichneten Dokumente sind zwingend hochzuladen, bei den übrigen bitte die Erklärung beachten!
            </p>
            <div className='antrag-ausbildung-header'
                onClick={() => toggleCollapse('personalData')}
                style={{ cursor: 'pointer' }}>
                Persönliche Daten {collapse.personalData ? <IoChevronDown /> : <IoChevronUp />}
            </div>
            <div
                style={{
                    maxHeight: collapse.personalData ? "0px" : "2000px",
                    overflow: "hidden",
                    transition: "max-height 0.3s ease",
                }}
            >
                <FileUpload
                    id='reisepass'
                    name='reisepass'
                    title='Reisepass'
                    label='Reisepass *'
                    description='Seite mit Foto und persönlichen Daten'
                    accept='.pdf,.png,.jpg'
                    onDelete={(fileName) => handleDelete(DOCUMENT_TYPES.REISEPASS, fileName)}
                    onUploadSuccess={(fileName, guid) => onUploadSuccess?.(DOCUMENT_TYPES.REISEPASS, fileName, guid)}
                    value={getDocumentsByDescription(DOCUMENT_TYPES.REISEPASS)}
                    antragId={antragId}
                    className={showValidation && (!getDocumentsByDescription(DOCUMENT_TYPES.REISEPASS) || getDocumentsByDescription(DOCUMENT_TYPES.REISEPASS).length === 0) ? 'error' : ''}
                    tabindex={isViewOnly ? -1 : 0}
                />

                <FileUpload
                    id='lebenslauf'
                    name='lebenslauf'
                    title='Lebenslauf'
                    label='Tabellarischer Lebenslauf *'
                    description='Deutsch oder Englisch'
                    accept='.pdf,.png,.jpg'
                    onDelete={(fileName) => handleDelete(DOCUMENT_TYPES.LEBENSLAUF, fileName)}
                    onUploadSuccess={(fileName, guid) => onUploadSuccess?.(DOCUMENT_TYPES.LEBENSLAUF, fileName, guid)}
                    value={getDocumentsByDescription(DOCUMENT_TYPES.LEBENSLAUF)}
                    antragId={antragId}
                    className={showValidation && (!getDocumentsByDescription(DOCUMENT_TYPES.LEBENSLAUF) || getDocumentsByDescription(DOCUMENT_TYPES.LEBENSLAUF).length === 0) ? 'error' : ''}
                    tabindex={isViewOnly ? -1 : 0}
                />
                <FileUpload
                    id='sprachnachweis'
                    name='sprachnachweis'
                    title='Sprachnachweis Deutsch'
                    label={`Sprachnachweis Deutsch (${bachDegree === 'Gesundheits- und Krankenpflege' ? 'B1' : 'B2'})`}
                    description={sprachnachweisDescription}
                    accept='.pdf,.png,.jpg'
                    onDelete={(fileName) => handleDelete(DOCUMENT_TYPES.SPRACHNACHWEIS, fileName)}
                    onUploadSuccess={(fileName, guid) => onUploadSuccess?.(DOCUMENT_TYPES.SPRACHNACHWEIS, fileName, guid)}
                    value={getDocumentsByDescription(DOCUMENT_TYPES.SPRACHNACHWEIS)}
                    antragId={antragId}
                    tabindex={isViewOnly ? -1 : 0}
                />
                <FileUpload
                    id='geburtsurkunde'
                    name='geburtsurkunde'
                    title='Geburtsurkunde'
                    label='Geburtsurkunde *'
                    description='Originaldokument + Übersetzung + Beglaubigung (falls notwendig) '
                    accept='.pdf,.png,.jpg'
                    onDelete={(fileName) => handleDelete(DOCUMENT_TYPES.GEBURTSURKUNDE, fileName)}
                    onUploadSuccess={(fileName, guid) => onUploadSuccess?.(DOCUMENT_TYPES.GEBURTSURKUNDE, fileName, guid)}
                    value={getDocumentsByDescription(DOCUMENT_TYPES.GEBURTSURKUNDE)}
                    antragId={antragId}
                    className={showValidation && (!getDocumentsByDescription(DOCUMENT_TYPES.GEBURTSURKUNDE) || getDocumentsByDescription(DOCUMENT_TYPES.GEBURTSURKUNDE).length === 0) ? 'error' : ''}
                    tabindex={isViewOnly ? -1 : 0}
                />
                <FileUpload
                    id='heiratsurkunde'
                    name='heiratsurkunde'
                    title='Heiratsurkunde'
                    label={`Heiratsurkunde/Urkunde über Namensänderung${familyStatus === 'geschieden' || familyStatus === 'verheiratet' ? ' *' : ''}`}
                    description='Originaldokument + Übersetzung + Beglaubigung (falls notwendig)'
                    accept='.pdf,.png,.jpg'
                    onDelete={(fileName) => handleDelete(DOCUMENT_TYPES.HEIRATSURKUNDE, fileName)}
                    onUploadSuccess={(fileName, guid) => onUploadSuccess?.(DOCUMENT_TYPES.HEIRATSURKUNDE, fileName, guid)}
                    value={getDocumentsByDescription(DOCUMENT_TYPES.HEIRATSURKUNDE)}
                    antragId={antragId}
                    tabindex={isViewOnly ? -1 : 0}
                />
                <FileUpload
                    id='strafregisterauszug'
                    name='strafregisterauszug'
                    title='Strafregisterauszug'
                    label='Strafregisterauszug'
                    description='Aus dem Land, wo Antragsteller*in die letzten 5 Jahre mindestens 6 Monate gelebt hat - nicht älter als 3 Monate<br />
                                spätestens vor Bescheiderstellung <br />
                                Originaldokument + Übersetzung + Beglaubigung (falls notwendig)'
                    accept='.pdf,.png,.jpg'
                    onDelete={(fileName) => handleDelete(DOCUMENT_TYPES.STRAFREGISTER, fileName)}
                    onUploadSuccess={(fileName, guid) => onUploadSuccess?.(DOCUMENT_TYPES.STRAFREGISTER, fileName, guid)}
                    value={getDocumentsByDescription(DOCUMENT_TYPES.STRAFREGISTER)}
                    antragId={antragId}
                    tabindex={isViewOnly ? -1 : 0}

                />

            </div>

            <div className='antrag-ausbildung-header' onClick={() => toggleCollapse('ausbildung')} style={{ cursor: 'pointer' }}>
                Ausbildung {collapse.ausbildung ? <IoChevronDown /> : <IoChevronUp />}
            </div>
            <div
                style={{
                    maxHeight: collapse.ausbildung ? "0px" : "2800px",
                    overflow: "hidden",
                    transition: "max-height 0.3s ease",
                }}
            >
                <FileUpload
                    id='postsecondaryinstitution'
                    name='postsecondaryinstitution'
                    title='Nachweis der Anerkennung als ausländische postsekundäre Bildungseinrichtung'
                    label='Nachweis der Anerkennung als ausländische postsekundäre Bildungseinrichtung'
                    description='zB. ANABIN <a href="https://anabin.kmk.org/cms/public/startseite">anabin: Infoportal zu ausländischen Bildungsabschlüssen </a> ,
                                ENIC NARIC Austria <a href="https://www.aais.at/">(www.aais.at)</a>'
                    accept='.pdf,.png,.jpg'
                    onDelete={(fileName) => handleDelete(DOCUMENT_TYPES.POSTSECONDARY, fileName)}
                    onUploadSuccess={(fileName, guid) => onUploadSuccess?.(DOCUMENT_TYPES.POSTSECONDARY, fileName, guid)}
                    value={getDocumentsByDescription(DOCUMENT_TYPES.POSTSECONDARY)}
                    antragId={antragId}
                    tabindex={isViewOnly ? -1 : 0}

                />
                <FileUpload
                    id='internshipcertificate'
                    name='internshipcertificate'
                    title='Praktikumsnachweise Studium'
                    label='Praktikumsnachweise Studium *'
                    description='Praktikumszeiten mit Stundenangaben, Institution, genaue Beschreibung der Tätigkeiten <br />
                                Originaldokument + Übersetzung + Beglaubigung (falls notwendig)'
                    accept='.pdf,.png,.jpg'
                    onDelete={(fileName) => handleDelete(DOCUMENT_TYPES.PRAKTIKUM, fileName)}
                    onUploadSuccess={(fileName, guid) => onUploadSuccess?.(DOCUMENT_TYPES.PRAKTIKUM, fileName, guid)}
                    value={getDocumentsByDescription(DOCUMENT_TYPES.PRAKTIKUM)}
                    antragId={antragId}
                    className={showValidation && (!getDocumentsByDescription(DOCUMENT_TYPES.PRAKTIKUM) || getDocumentsByDescription(DOCUMENT_TYPES.PRAKTIKUM).length === 0) ? 'error' : ''}
                    tabindex={isViewOnly ? -1 : 0}

                />
                <FileUpload
                    id='thesis'
                    name='thesis'
                    title='Bachelor-/Abschlussarbeit'
                    label={`Bachelor-/Abschlussarbeit${hasThesis ? ' *' : ''}`}
                    description='Wenn vorhanden gesamte Arbeit im Original und Inhaltsangabe oder Abstract in Deutsch oder Englisch'
                    accept='.pdf,.png,.jpg'
                    onDelete={(fileName) => handleDelete(DOCUMENT_TYPES.THESIS, fileName)}
                    onUploadSuccess={(fileName, guid) => onUploadSuccess?.(DOCUMENT_TYPES.THESIS, fileName, guid)}
                    value={getDocumentsByDescription(DOCUMENT_TYPES.THESIS)}
                    antragId={antragId}
                    className={showValidation && hasThesis && (!getDocumentsByDescription(DOCUMENT_TYPES.THESIS) || getDocumentsByDescription(DOCUMENT_TYPES.THESIS).length === 0) ? 'error' : ''}
                    tabindex={isViewOnly ? -1 : 0}

                />
                <FileUpload
                    id='curriculum'
                    name='curriculum'
                    title='Curriculum'
                    label='Curriculum *'
                    description='Detaillierte Beschreibung der Lehrinhalte pro besuchte Lehrveranstaltung, Stundenausmaß, ECTS, Syllabi, Studienprogramm <br />
                                Eine Auflistung an Kursen reicht <b>nicht</b> aus <br />
                                Finden Sie <a href="curriculum">hier</a> ein Beispiel<br />
                                Originaldokument + Übersetzung'
                    accept='.pdf,.png,.jpg'
                    onDelete={(fileName) => handleDelete(DOCUMENT_TYPES.CURRICULUM, fileName)}
                    onUploadSuccess={(fileName, guid) => onUploadSuccess?.(DOCUMENT_TYPES.CURRICULUM, fileName, guid)}
                    value={getDocumentsByDescription(DOCUMENT_TYPES.CURRICULUM)}
                    antragId={antragId}
                    className={showValidation && (!getDocumentsByDescription(DOCUMENT_TYPES.CURRICULUM) || getDocumentsByDescription(DOCUMENT_TYPES.CURRICULUM).length === 0) ? 'error' : ''}
                    tabindex={isViewOnly ? -1 : 0}

                />
                <FileUpload
                    id='maturacertificate'
                    name='maturacertificate'
                    title='Reife-/Maturazeugnis'
                    label='Reife-/Maturazeugnis *'
                    description='Urkunde über allgemeine Universitätsreife <br />
                                Originaldokument + Übersetzung + Beglaubigung (falls notwendig)'
                    accept='.pdf,.png,.jpg'
                    onDelete={(fileName) => handleDelete(DOCUMENT_TYPES.MATURA, fileName)}
                    onUploadSuccess={(fileName, guid) => onUploadSuccess?.(DOCUMENT_TYPES.MATURA, fileName, guid)}
                    value={getDocumentsByDescription(DOCUMENT_TYPES.MATURA)}
                    antragId={antragId}
                    className={showValidation && (!getDocumentsByDescription(DOCUMENT_TYPES.MATURA) || getDocumentsByDescription(DOCUMENT_TYPES.MATURA).length === 0) ? 'error' : ''}
                    tabindex={isViewOnly ? -1 : 0}

                />
                <FileUpload
                    id='diplomacertificate'
                    name='diplomacertificate'
                    title='Abschluss bzw. Diplomurkunde des Studiums, Diploma Supplement'
                    label='Abschluss bzw. Diplomurkunde des Studiums, Diploma Supplement *'
                    description='Originaldokument + Übersetzung + Beglaubigung (falls notwendig)'
                    accept='.pdf,.png,.jpg'
                    onDelete={(fileName) => handleDelete(DOCUMENT_TYPES.DIPLOM, fileName)}
                    onUploadSuccess={(fileName, guid) => onUploadSuccess?.(DOCUMENT_TYPES.DIPLOM, fileName, guid)}
                    value={getDocumentsByDescription(DOCUMENT_TYPES.DIPLOM)}
                    antragId={antragId}
                    className={showValidation && (!getDocumentsByDescription(DOCUMENT_TYPES.DIPLOM) || getDocumentsByDescription(DOCUMENT_TYPES.DIPLOM).length === 0) ? 'error' : ''}
                    tabindex={isViewOnly ? -1 : 0}

                />
                <FileUpload
                    id='certificates'
                    name='certificates'
                    title='Transcript of Records'
                    label='Transcript of Records *'
                    description='Über die abgelegten Prüfungen und Bezeichnungen der besuchten Lehrveranstaltungen <br />
                                Originaldokument + Übersetzung + Beglaubigung (falls notwendig)'
                    accept='.pdf,.png,.jpg'
                    onDelete={(fileName) => handleDelete(DOCUMENT_TYPES.TRANSCRIPT, fileName)}
                    onUploadSuccess={(fileName, guid) => onUploadSuccess?.(DOCUMENT_TYPES.TRANSCRIPT, fileName, guid)}
                    value={getDocumentsByDescription(DOCUMENT_TYPES.TRANSCRIPT)}
                    antragId={antragId}
                    className={showValidation && (!getDocumentsByDescription(DOCUMENT_TYPES.TRANSCRIPT) || getDocumentsByDescription(DOCUMENT_TYPES.TRANSCRIPT).length === 0) ? 'error' : ''}
                    tabindex={isViewOnly ? -1 : 0}

                />
            </div>
            <div className='antrag-ausbildung-header' onClick={() => toggleCollapse('workExperience')} style={{ cursor: 'pointer' }}>
                Berufserfahrung {collapse.workExperience ? <IoChevronDown /> : <IoChevronUp />}
            </div>
            <div
                style={{
                    maxHeight: collapse.workExperience ? "0px" : "800px",
                    overflow: "hidden",
                    transition: "max-height 0.3s ease",
                }}
            >
                <FileUpload
                    id='registrationcertificate'
                    name='registrationcertificate'
                    title='Registration Certificate, Berufslizenz, Eintragung ins Berufsregister'
                    label={`Registration Certificate, Berufslizenz, Eintragung ins Berufsregister${hasExam ? ' *' : ''}`}
                    description='Originaldokument + Übersetzung + Beglaubigung (falls notwendig)'
                    accept='.pdf,.png,.jpg'
                    onDelete={(fileName) => handleDelete(DOCUMENT_TYPES.REGISTRATION, fileName)}
                    onUploadSuccess={(fileName, guid) => onUploadSuccess?.(DOCUMENT_TYPES.REGISTRATION, fileName, guid)}
                    value={getDocumentsByDescription(DOCUMENT_TYPES.REGISTRATION)}
                    antragId={antragId}
                    className={showValidation && hasExam && (!getDocumentsByDescription(DOCUMENT_TYPES.REGISTRATION) || getDocumentsByDescription(DOCUMENT_TYPES.REGISTRATION).length === 0) ? 'error' : ''}
                    tabindex={isViewOnly ? -1 : 0}

                />
                <FileUpload
                    id='workcertificates'
                    name='workcertificates'
                    title='Arbeitsbestätigung nach Studium'
                    label={`Arbeitsbestätigung nach Studium${hasWorkExperience ? ' *' : ''}`}
                    description='Zeitraum, Wochenarbeitsstunden, Institution, genaue Beschreibung der Tätigkeiten <br />
                                        Originaldokument + Übersetzung'
                    accept='.pdf,.png,.jpg'
                    onDelete={(fileName) => handleDelete(DOCUMENT_TYPES.ARBEITSBESTAETIGUNG, fileName)}
                    onUploadSuccess={(fileName, guid) => onUploadSuccess?.(DOCUMENT_TYPES.ARBEITSBESTAETIGUNG, fileName, guid)}
                    value={getDocumentsByDescription(DOCUMENT_TYPES.ARBEITSBESTAETIGUNG)}
                    antragId={antragId}
                    className={showValidation && hasWorkExperience && (!getDocumentsByDescription(DOCUMENT_TYPES.ARBEITSBESTAETIGUNG) || getDocumentsByDescription(DOCUMENT_TYPES.ARBEITSBESTAETIGUNG).length === 0) ? 'error' : ''}
                    tabindex={isViewOnly ? -1 : 0}

                />
            </div>
            <div className='antrag-ausbildung-header' onClick={() => toggleCollapse('other')} style={{ cursor: 'pointer' }}>
                Optionale Dokumente {collapse.other ? <IoChevronDown /> : <IoChevronUp />}
            </div>
            <div
                style={{
                    maxHeight: collapse.other ? "0px" : `${calculateMaxHeight(otherDocumentData.length)}px`,
                    overflow: "hidden",
                    transition: "max-height 0.3s ease",
                }}
            >
                <p style={{ textAlign: "left", marginLeft: "4px" }}>Hier können Sie bei Bedarf noch weitere Dokumente hochladen</p>
                {otherDocumentData.filter(doc => doc.description !== 'Vollmacht').map((data, index) => (
                    <OtherDocumentForm
                        key={index}
                        index={index}
                        formData={data}
                        onChange={(e) => handleOtherDocumentChange(index, e)}
                        handleDelete={() => handleDeleteOther(index)}
                        isViewOnly={isViewOnly}
                        antragId={antragId}
                        onUploadSuccess={(fileName, guid) => handleOtherDocumentUploadSuccess(index, fileName, guid)}
                    />
                ))}
                <div className='degree-buttons'>
                    {otherDocumentData.length > 1 && (
                        <button tabIndex={isViewOnly ? -1 : 0} className='add-degree-button' type='button' onClick={handleOtherDocumentDelete}>
                            <IoIosRemove />  Optionales Dokument entfernen
                        </button>
                    )}
                    {otherDocumentData.length < 8 && (
                        <button tabIndex={isViewOnly ? -1 : 0} className='add-degree-button' type='button' onClick={addOtherDocument}>
                            <IoIosAdd /> Optionales Dokument hinzufügen
                        </button>
                    )}
                </div>
            </div>
        </div>

    );
}

export default DocumentForm;
